import React, { useMemo } from 'react';
import './progress.css';
import { isLightColor } from './utils';

// Format numbers with commas
const formatNumber = (num) => num.toLocaleString();

// Function to generate dynamic milestones based on current points
const generateMilestones = (currentPoints) => {
  // Ensure currentPoints is a valid number
  const points = Math.max(0, currentPoints || 0);

  // If points are 0, return default milestones
  if (points === 0) {
    return [0, 25, 50, 75, 100]; // Default milestones when no progress
  }

  // Determine appropriate scale based on size of current points
  let scale = 1;
  if (points >= 10000) {
    scale = 5000;
  } else if (points >= 5000) {
    scale = 2500;
  } else if (points >= 2000) {
    scale = 1000;
  } else if (points >= 1000) {
    scale = 500;
  } else if (points >= 500) {
    scale = 250;
  } else if (points >= 200) {
    scale = 100;
  } else if (points >= 100) {
    scale = 50;
  } else if (points >= 50) {
    scale = 25;
  } else {
    scale = 10;
  }

  // Calculate a center-point that places current points near middle
  // We want to have current points fall around the 50-60% mark of the range
  let centerValue = Math.ceil(points / scale) * scale;

  // Adjust to make the user's points fall more toward the center
  // If points are close to the upper bound, we need to increase the scale
  if (points > centerValue * 0.8) {
    centerValue += scale;
  }

  // Calculate the maximum (ensure it's a nice round number)
  const maximum = centerValue * 1.6; // This places current points around 60% mark
  const roundedMax = Math.ceil(maximum / scale) * scale;

  // Calculate evenly spaced, round milestones
  const milestoneCount = 5; // We want 5 milestones total
  const stepSize = roundedMax / (milestoneCount - 1);

  const milestones = [];
  // Round to the finer of scale vs stepSize so low balances don't collapse
  // labels (e.g. stepSize 5 rounded to scale 10 becomes 0, 10, 10, 20, 20).
  const roundingStep = Math.min(scale, stepSize);
  for (let i = 0; i < milestoneCount; i++) {
    const milestone = Math.round((i * stepSize) / roundingStep) * roundingStep;
    milestones.push(milestone);
  }

  return milestones;
};

const placeHolderColor = 'rgb(113, 113, 113)';

const ProgressComponent = ({
  currentPoints,
  maxPoints,
  fillColor = placeHolderColor,
  themeBackgroundColor = fillColor,
  labelFontSize = '11px',
  height = '13px',
  isLoadingPoints = false,
}) => {
  // Ensure valid numbers
  const current = Math.max(0, currentPoints || 0);

  // Generate dynamic milestones based on current points
  // Use default milestones when loading
  const dynamicMilestones = useMemo(() => {
    if (isLoadingPoints) {
      return [0, 25, 50, 75, 100]; // Default milestones when loading
    }
    return generateMilestones(current);
  }, [current, isLoadingPoints]);

  // Use the maximum milestone as maxPoints if not provided
  const max = maxPoints || dynamicMilestones[dynamicMilestones.length - 1];

  // Create segment data for the progress bar
  const segments = useMemo(() => {
    // Create segments based on milestones
    return dynamicMilestones.slice(0, -1).map((milestone, index) => {
      const nextMilestone = dynamicMilestones[index + 1];
      const segmentMin = milestone;
      const segmentMax = nextMilestone;

      // Determine fill state of this segment
      let fillPercentage = 0;

      // If current points is 0, ensure all segments have 0 fill
      if (current === 0) {
        fillPercentage = 0;
      } else if (current >= segmentMax) {
        // Segment is completely filled
        fillPercentage = 100;
      } else if (current <= segmentMin) {
        // Segment is completely empty
        fillPercentage = 0;
      } else {
        // Segment is partially filled
        fillPercentage =
          ((current - segmentMin) / (segmentMax - segmentMin)) * 100;
      }

      return {
        min: segmentMin,
        max: segmentMax,
        fillPercentage,
        // This segment contains the current progress point
        isCurrentSegment: current >= segmentMin && current < segmentMax,
      };
    });
  }, [current, dynamicMilestones]);

  // Check if we need border for light theme colors
  const needsBorder = segments.some(
    (segment) =>
      segment.fillPercentage === 100 && isLightColor(themeBackgroundColor)
  );

  const progressBarContainerStyle = {
    height,
    display: 'flex',
    borderRadius: '12px',
    overflow: 'hidden',
    ...(needsBorder && {
      border: '1px solid rgba(0, 0, 0, 0.15)',
      boxSizing: 'border-box',
    }),
  };

  return (
    <div className='progress-component'>
      <div className='progress-bar-container' style={progressBarContainerStyle}>
        {segments.map((segment, index) => {
          const isLastSegmentWithProgress =
            segment.isCurrentSegment && segment.fillPercentage < 100;

          // Use themeBackgroundColor for completely filled segments, fillColor for partially filled
          const segmentColor =
            segment.fillPercentage === 100 ? themeBackgroundColor : fillColor;

          const segmentStyle = {
            flex: 1,
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor:
              segment.fillPercentage === 100
                ? themeBackgroundColor
                : 'rgb(216, 216, 216)', // Apply theme background color to completely filled segments
          };

          // Add border radius to first and last segment
          if (index === 0) {
            segmentStyle.borderRadius = '12px 0 0 12px';
          } else if (index === segments.length - 1) {
            segmentStyle.borderRadius = '0 12px 12px 0';
          }

          // For loading state, show placeholder fill
          if (isLoadingPoints) {
            const loadingFillStyle = {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: placeHolderColor,
              opacity: 0.5,
            };

            if (index === 0) {
              loadingFillStyle.borderRadius = '12px 0 0 12px';
            } else if (index === segments.length - 1) {
              loadingFillStyle.borderRadius = '0 12px 12px 0';
            }

            return (
              <div
                key={`segment-${index}`}
                className='progress-segment'
                style={segmentStyle}
              >
                <div
                  className='segment-fill loading'
                  style={loadingFillStyle}
                />
              </div>
            );
          }

          // For zero progress, keep all segments empty
          if (current === 0) {
            return (
              <div
                key={`segment-${index}`}
                className='progress-segment'
                style={segmentStyle}
              />
            );
          }

          // For completely filled segments, we'll extend the fill to include a "deadzone"
          // For partially filled segments, we'll keep the actual percentage
          // This creates the illusion that the fill extends beyond where it actually should
          let actualFillPercentage;
          let deadzoneWidth = 0;

          if (segment.fillPercentage === 100) {
            // For filled segments, we fill 70% and add a 30% deadzone that looks the same
            actualFillPercentage = 70;
            deadzoneWidth = 30;
          } else {
            // For partially filled segments, scale the fill to fit within 70% of the segment
            actualFillPercentage = (segment.fillPercentage * 70) / 100;
          }

          const fillStyle = {
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${actualFillPercentage}%`,
            height: '100%',
            backgroundColor: segmentColor,
          };

          // Add border radius to the fill if it's the last segment with progress
          if (isLastSegmentWithProgress) {
            fillStyle.borderRadius = '0 12px 12px 0';
          } else if (index === 0 && segment.fillPercentage > 0) {
            fillStyle.borderRadius = '12px 0 0 12px';
          }

          // Style for the deadzone that creates the illusion that the fill extends to the label
          const deadzoneStyle = {
            position: 'absolute',
            top: 0,
            left: `${actualFillPercentage}%`,
            width: `${deadzoneWidth}%`,
            height: '100%',
            backgroundColor: segmentColor,
          };

          return (
            <div
              key={`segment-${index}`}
              className='progress-segment'
              style={segmentStyle}
            >
              <div className='segment-fill' style={fillStyle} />

              {/* Only add deadzone for completely filled segments */}
              {deadzoneWidth > 0 && (
                <div className='segment-deadzone' style={deadzoneStyle} />
              )}
            </div>
          );
        })}
      </div>
      <div className='progress-labels-container'>
        {dynamicMilestones.map((point, index) => {
          const labelStyle = {
            color: placeHolderColor,
            fontSize: labelFontSize,
          };

          // Display placeholder value for loading state
          let displayValue;
          if (isLoadingPoints) {
            displayValue = '—'; // Em dash as placeholder
          } else {
            displayValue =
              current === 0 && point === 0 ? '0' : formatNumber(point);
          }

          return (
            <span key={point} className='progress-label' style={labelStyle}>
              {displayValue}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressComponent;
